import { useEffect, useState } from "react";
import axios from "axios";
import { X, Check, ExternalLink, Banknote, ArrowRight, Clapperboard, PenLine, Scissors, Sparkles, Lock, Youtube } from "lucide-react";
import { ROLE_DETAILS, API } from "@/constants/roles";

const ICONS = {
  "Standard Animator": Clapperboard,
  "High-Quality Animator": Clapperboard,
  "Script Writer": PenLine,
  "Video Editor": Scissors,
};

const LinkList = ({ items, testidPrefix }) => (
  <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
    {items.map((ex) => (
      <a
        key={ex.link || ex.url}
        data-testid={`${testidPrefix}-${ex.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 48)}`}
        href={ex.link || ex.url}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex items-center justify-between gap-3 rounded-2xl border border-slate-200/80 px-4 py-3 transition-colors hover:border-cyan-300 hover:bg-cyan-50/50"
      >
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-slate-900 group-hover:text-cyan-700">{ex.title}</span>
          {ex.note && <span className="block truncate text-xs text-slate-500">{ex.note}</span>}
        </span>
        <ExternalLink className="h-4 w-4 shrink-0 text-slate-300 group-hover:text-cyan-500" />
      </a>
    ))}
  </div>
);

// Style & quality references + channel examples are owner-managed (Admin → Settings → Hiring references).
const useHiringReferences = (roleName) => {
  const [refs, setRefs] = useState(null);
  useEffect(() => {
    if (!roleName) return;
    setRefs(null);
    axios.get(`${API}/hiring-references?role=${encodeURIComponent(roleName)}`)
      .then((r) => setRefs(r.data))
      .catch(() => setRefs({ style: [], channel: [] }));
  }, [roleName]);
  return refs;
};

export const RoleDetailModal = ({ roleName, isClosed, onClose, onApply }) => {
  const refs = useHiringReferences(roleName);
  useEffect(() => {
    if (!roleName) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [roleName, onClose]);

  if (!roleName) return null;
  const role = ROLE_DETAILS[roleName];
  if (!role) return null;
  const Icon = ICONS[roleName];

  return (
    <div
      data-testid="role-detail-modal"
      className="overlay-in fixed inset-0 z-[60] flex items-end justify-center bg-slate-900/40 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
    >
      <div
        className="modal-in max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-white p-7 shadow-2xl sm:rounded-3xl sm:p-10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-500 text-white">
              <Icon className="h-7 w-7" />
            </span>
            <div>
              <p className="font-mono-label text-[10px] font-bold uppercase tracking-widest text-cyan-600">{role.category}</p>
              <h3 data-testid="role-modal-title" className="font-display text-2xl font-bold tracking-tight text-slate-900">
                {roleName}
              </h3>
            </div>
          </div>
          <button
            data-testid="role-modal-close-btn"
            onClick={onClose}
            className="rounded-full bg-slate-100 p-2.5 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-800"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {isClosed && (
          <div data-testid="role-modal-closed-note" className="mt-6 flex items-center gap-2.5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-600">
            <Lock className="h-4 w-4 shrink-0" />
            Applications for this role are currently closed — check back soon.
          </div>
        )}

        <p className="mt-6 text-base leading-relaxed text-slate-600">{role.tagline}</p>

        <div className="mt-8">
          <h4 className="font-display text-sm font-bold uppercase tracking-wide text-slate-900">What we're looking for</h4>
          <ul className="mt-4 space-y-3">
            {role.lookingFor.map((item) => (
              <li key={item} className="flex items-start gap-3 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan-500" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        {role.creativeFreedom && (
          <div className="mt-8 rounded-2xl border border-cyan-200/70 bg-cyan-50/60 p-5">
            <h4 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-slate-900">
              <Sparkles className="h-4 w-4 text-cyan-600" /> Creative freedom
            </h4>
            <p data-testid="role-modal-creative-freedom" className="mt-3 text-sm leading-relaxed text-slate-600">
              {role.creativeFreedom}
            </p>
          </div>
        )}

        {refs && refs.style && refs.style.length > 0 && (
          <div data-testid="role-modal-style-references" className="mt-8">
            <h4 className="font-display text-sm font-bold uppercase tracking-wide text-slate-900">Style & quality references</h4>
            <p className="mt-2 text-xs text-slate-500">
              The visual direction, animation quality, pacing, and camera work we're aiming toward — references, not templates to copy.
            </p>
            <LinkList items={refs.style} testidPrefix="style-reference" />
          </div>
        )}

        {refs && refs.channel && refs.channel.length > 0 && (
          <div data-testid="role-modal-channel-examples" className="mt-8">
            <h4 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-slate-900">
              <Youtube className="h-4 w-4 text-cyan-600" /> Channel examples · content direction
            </h4>
            <p className="mt-2 text-xs text-slate-500">
              The type of content and storytelling that performs on @ItsMelto — direction for tone, not a quality ceiling.
            </p>
            <LinkList items={refs.channel} testidPrefix="channel-example" />
          </div>
        )}

        <div className="mt-8">
          <h4 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-slate-900">
            <Banknote className="h-4 w-4 text-cyan-600" /> Pay breakdown
          </h4>
          <div className="mt-4 divide-y divide-slate-100 rounded-2xl border border-slate-200/80">
            {role.payRows.map((row) => (
              <div key={row.label} className="flex items-center justify-between gap-4 px-4 py-3.5">
                <span className="text-sm text-slate-600">{row.label}</span>
                <span className="text-right text-sm font-bold text-slate-900">{row.value}</span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-400">Paid per job. Exact pay is shown on each job on your dashboard before you take it.</p>
        </div>

        {isClosed ? (
          <div
            data-testid="role-modal-apply-closed"
            className="mt-9 flex w-full items-center justify-center gap-2 rounded-full bg-slate-100 py-4 text-base font-semibold text-slate-400"
          >
            <Lock className="h-5 w-5" />
            Applications closed for this role
          </div>
        ) : (
          <button
            data-testid="role-modal-apply-btn"
            onClick={() => onApply(roleName)}
            className="group mt-9 flex w-full items-center justify-center gap-2 rounded-full bg-cyan-500 py-4 text-base font-semibold text-white shadow-lg shadow-cyan-500/25 transition-all duration-200 hover:bg-cyan-600 hover:shadow-cyan-500/40"
          >
            Apply as {roleName}
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
          </button>
        )}
      </div>
    </div>
  );
};
